import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { UserPlus, Mail, Send, Loader2, X, Check, Clock, RefreshCw, Trash2, AlertCircle, CheckCircle, Info } from 'lucide-react';
import { toast } from 'sonner';

import { API } from '../config/api';

export default function PlayerInvite({ teams = [], onInviteSent }) {
  const [showModal, setShowModal] = useState(false);
  const [sending, setSending] = useState(false);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    team_id: '',
    team_name: ''
  });

  useEffect(() => {
    if (showModal) fetchPendingInvites();
  }, [showModal]);

  const fetchPendingInvites = async () => {
    try {
      const res = await axios.get(`${API}/invitations/pending`);
      setPendingInvites(res.data.invitations || []);
    } catch (err) {
      console.error('Failed to fetch invites');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.first_name || !formData.last_name || !formData.email) {
      toast.error('Please fill in name and email');
      return;
    }

    setSending(true);
    try {
      // Find team name if team_id is selected
      const selectedTeam = teams.find(t => t.id === formData.team_id);
      
      await axios.post(`${API}/invitations/invite`, {
        ...formData,
        team_name: selectedTeam?.name || ''
      });
      
      toast.success(`Invitation sent to ${formData.email}!`);
      setFormData({ first_name: '', last_name: '', email: '', team_id: '', team_name: '' });
      fetchPendingInvites();
      if (onInviteSent) onInviteSent();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send invitation');
    } finally {
      setSending(false);
    }
  };

  const resendInvite = async (inviteId) => {
    try {
      await axios.post(`${API}/invitations/resend/${inviteId}`);
      toast.success('Invitation resent!');
      fetchPendingInvites();
    } catch (err) {
      toast.error('Failed to resend');
    }
  };

  const cancelInvite = async (inviteId) => {
    try {
      await axios.delete(`${API}/invitations/${inviteId}`);
      toast.success('Invitation cancelled');
      fetchPendingInvites();
    } catch (err) {
      toast.error('Failed to cancel');
    }
  };

  return (
    <>
      <Button
        onClick={() => setShowModal(true)}
        className="h-10 w-10 p-0 bg-gradient-to-r from-[#02b5ff] to-[#0090cc] text-white rounded-full"
        data-testid="invite-player-btn"
        title="Invite Player"
      >
        <UserPlus className="h-5 w-5" />
      </Button>

      {showModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl max-w-lg w-full max-h-[90vh] overflow-hidden">
            {/* Header */}
            <div className="p-4 border-b border-zinc-800 flex items-center justify-between">
              <h2 className="text-lg font-bold text-white flex items-center gap-2">
                <UserPlus className="h-5 w-5 text-[#02b5ff]" />
                Invite New Player
              </h2>
              <button onClick={() => setShowModal(false)} className="text-zinc-500 hover:text-white">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-4 space-y-4 max-h-[70vh] overflow-y-auto">
              {/* Invite Form */}
              <form onSubmit={handleSubmit} className="space-y-4">
                <p className="text-sm text-zinc-400">
                  Enter the player's name and email. They'll receive an invitation to register and complete their profile.
                </p>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-zinc-500 mb-1 block">First Name *</label>
                    <input
                      type="text"
                      value={formData.first_name}
                      onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm"
                      placeholder="John"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-xs text-zinc-500 mb-1 block">Last Name *</label>
                    <input
                      type="text"
                      value={formData.last_name}
                      onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm"
                      placeholder="Smith"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="text-xs text-zinc-500 mb-1 block">Email Address *</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm"
                    placeholder="player@email.com"
                    required
                  />
                </div>

                {teams.length > 0 && (
                  <div>
                    <label className="text-xs text-zinc-500 mb-1 block">Assign to Team (Optional)</label>
                    <select
                      value={formData.team_id}
                      onChange={(e) => setFormData({ ...formData, team_id: e.target.value })}
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm"
                    >
                      <option value="">No team yet</option>
                      {teams.map(team => (
                        <option key={team.id} value={team.id}>{team.name}</option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="flex gap-3">
                  <Button
                    type="button"
                    onClick={() => setShowModal(false)}
                    variant="outline"
                    className="flex-1 border-zinc-700 text-zinc-400 hover:text-white hover:bg-zinc-800"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={sending}
                    className="flex-1 bg-[#02b5ff] hover:bg-[#02b5ff]/80"
                  >
                    {sending ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending...</>
                    ) : (
                      <><Send className="h-4 w-4 mr-2" /> Send Invitation</>
                    )}
                  </Button>
                </div>
              </form>

              {/* Pending Invitations */}
              {pendingInvites.length > 0 && (
                <div className="border-t border-zinc-800 pt-4">
                  <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                    <Clock className="h-4 w-4 text-yellow-500" />
                    Pending Invitations ({pendingInvites.length})
                  </h3>
                  <div className="space-y-2">
                    {pendingInvites.map(invite => {
                      const emailStatus = invite.email_status || {};
                      const emailSent = emailStatus.sent === true;
                      const emailError = emailStatus.error;
                      const emailPending = emailStatus.pending === true;
                      
                      return (
                        <div key={invite.id} className="bg-zinc-800/50 rounded-lg p-3">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-white text-sm font-medium">
                                {invite.first_name} {invite.last_name}
                              </p>
                              <p className="text-xs text-zinc-500">{invite.email}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => resendInvite(invite.id)}
                                className="p-1.5 text-zinc-400 hover:text-[#02b5ff] transition-colors"
                                title="Resend"
                              >
                                <RefreshCw className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => cancelInvite(invite.id)}
                                className="p-1.5 text-zinc-400 hover:text-red-500 transition-colors"
                                title="Cancel"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                          
                          {/* Email Status */}
                          <div className="mt-2 pt-2 border-t border-zinc-700/50">
                            {emailSent ? (
                              <div className="flex items-center gap-1 text-xs text-green-400">
                                <CheckCircle className="h-3 w-3" />
                                Email sent successfully
                                {emailStatus.resend_id && (
                                  <span className="text-zinc-500 ml-1">ID: {emailStatus.resend_id.slice(0, 8)}...</span>
                                )}
                              </div>
                            ) : emailError ? (
                              <div className="text-xs">
                                <div className="flex items-center gap-1 text-red-400">
                                  <AlertCircle className="h-3 w-3" />
                                  Email delivery failed
                                </div>
                                <p className="text-zinc-500 mt-1">{emailStatus.error_detail || emailError}</p>
                              </div>
                            ) : emailPending ? (
                              <div className="flex items-center gap-1 text-xs text-yellow-400">
                                <Loader2 className="h-3 w-3 animate-spin" />
                                Sending email...
                              </div>
                            ) : (
                              <div className="flex items-center gap-1 text-xs text-zinc-500">
                                <Info className="h-3 w-3" />
                                Email status unknown - try resending
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  
                  {/* Email Configuration Note */}
                  <div className="mt-3 p-2 bg-zinc-800/30 rounded-lg">
                    <p className="text-[10px] text-zinc-500 flex items-start gap-1">
                      <Info className="h-3 w-3 mt-0.5 flex-shrink-0" />
                      If emails aren't being received, check spam folders. In sandbox mode, emails can only be sent to verified addresses.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
